/**
 * A programme's workflow state, as a pill that says what it means.
 *
 * The state was rendered as a bare `secondary` badge in some places and a
 * colour-mapped one in others, so the same programme read differently from one
 * screen to the next. The tone comes from `status-tones`, the one place that
 * decides, and an icon carries the meaning for anyone who cannot use colour.
 *
 * @module components/shared/WorkflowStateBadge
 */

import React from "react";

import { workflowStateTone } from "@/components/shared/status-tones";
import { TONE_BADGE, TONE_ICON } from "@/components/shared/tone-presentation";
import { Badge, Icon } from "@/components/ui";
import type { WorkflowState } from "@/server/types/workflow";

/**
 * States sharing a tone that must still read apart, by icon alone: every state
 * is one soft badge. Draft and Archived are both at rest, but one is live work
 * and the other is retired.
 */
const STATE_ICON: Partial<Record<WorkflowState, string>> = {
  Draft: "pencil-simple-line",
  Archived: "archive",
};

export interface WorkflowStateBadgeProps {
  state: WorkflowState;
  size?: "xs" | "sm" | "md";
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <WorkflowStateBadge state={programme.workflowState} size="sm" />
 * ```
 */
export function WorkflowStateBadge({
  state,
  size = "sm",
  className = "",
  "data-testid": testId,
}: WorkflowStateBadgeProps): React.ReactElement {
  const tone = workflowStateTone(state);
  return (
    <Badge tone={TONE_BADGE[tone]} size={size} className={className} data-testid={testId}>
      <Icon name={STATE_ICON[state] ?? TONE_ICON[tone]} aria-hidden />
      {state}
    </Badge>
  );
}
